import { useEffect, useState } from 'react';
import api from '../api';
import { AnimatedBackground, ModernCard, ModernBadge, ModernButton } from './ui/modern';

const ServiceMonitorPage = () => {
  const [statuses, setStatuses] = useState({});
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');

  const fetchStatuses = async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/services/status');
      setStatuses(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatuses();
  }, []);

  const handleRestart = async (name) => {
    try {
      await api.post(`/admin/services/${name}/restart`);
      setActionMsg(`Restart command sent for ${name}`);
      fetchStatuses();
    } catch (e) {
      console.error(e);
      setActionMsg(`Error: ${e.response?.data?.error || e.message}`);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6">
        <AnimatedBackground />
        <div className="relative z-10 max-w-7xl mx-auto">
          <ModernCard>
            <p className="text-gray-700 dark:text-slate-300">Loading service statuses...</p>
          </ModernCard>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gradient-to-br dark:from-slate-900 dark:via-slate-800 dark:to-slate-900 p-6">
      <AnimatedBackground />
      
      <div className="relative z-10 max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <ModernCard>
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
                <span className="text-3xl">🖥️</span> Service Monitor
              </h1>
              <p className="text-gray-600 dark:text-slate-400">Monitor and manage system services</p>
            </div>
            <ModernButton variant="glass" onClick={fetchStatuses}>
              🔄 Refresh
            </ModernButton>
          </div>
        </ModernCard>

        {/* Action Message */}
        {actionMsg && (
          <ModernCard className="bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-500/20">
            <p className="text-green-700 dark:text-green-300">{actionMsg}</p>
          </ModernCard>
        )}

        {/* Services Table */}
        <ModernCard>
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="text-gray-600 dark:text-slate-400 text-sm border-b border-gray-200 dark:border-white/10">
                  <th className="py-3 px-4">Service</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(statuses).map(([name, status]) => (
                  <tr key={name} className="border-t border-gray-200 dark:border-white/10 hover:bg-gray-50 dark:hover:bg-white/5">
                    <td className="py-3 px-4 font-semibold text-gray-900 dark:text-white">{name}</td>
                    <td className="py-3 px-4">
                      <ModernBadge 
                        variant={
                          status.toLowerCase() === 'running' ? 'success' : 
                          status.toLowerCase() === 'stopped' ? 'danger' : 
                          'secondary'
                        }
                      >
                        {status}
                      </ModernBadge>
                    </td>
                    <td className="py-3 px-4 text-right">
                      <ModernButton variant="primary" onClick={() => handleRestart(name)}>
                        Restart
                      </ModernButton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="md:hidden space-y-3 p-4">
            {Object.entries(statuses).map(([name, status]) => (
              <div key={name} className="border border-gray-200 dark:border-white/10 rounded-xl p-4 space-y-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="text-gray-900 dark:text-white font-semibold">{name}</div>
                  <ModernBadge
                    variant={
                      status.toLowerCase() === 'running' ? 'success' :
                      status.toLowerCase() === 'stopped' ? 'danger' :
                      'secondary'
                    }
                  >
                    {status}
                  </ModernBadge>
                </div>
                <ModernButton variant="primary" onClick={() => handleRestart(name)} className="w-full">
                  Restart
                </ModernButton>
              </div>
            ))}
          </div>
        </ModernCard>
      </div>
    </div>
  );
};

export default ServiceMonitorPage; 